import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Truck, Package, MapPin, User, Phone, Mail, Calendar, Clock,
  Home, Building2, ArrowRight, Calculator, FileText, CheckCircle,
  AlertCircle, Box, Scale, Ruler, ChevronDown, ChevronUp, Loader2,
  ArrowLeft, Briefcase
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_imageblaze/artifacts/5e5w021j_7D777DBC-744F-44B3-9C70-7AE1E6146D31.png";

const QuotePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Quote type from URL params or selection
  const initialType = searchParams.get('type'); // 'delivery' or 'moving'
  const [quoteType, setQuoteType] = useState(initialType);
  
  // Form state with ALL required fields
  const [formData, setFormData] = useState({
    // Client info
    client: {
      name: '',
      email: '',
      phone: '',
      preferred_date: '',
      preferred_time_slot: ''
    },
    
    // ===== DELIVERY SPECIFIC =====
    // Pickup address - COMPLETE
    pickup_address: '',
    pickup_postal_code: '',
    pickup_city: '',
    pickup_location_type: 'appartement', // maison, appartement, entreprise
    pickup_floor: 0,
    pickup_elevator: false,
    pickup_from_floor: false, // Récupération sur pallier
    
    // Delivery address - COMPLETE
    delivery_address: '',
    delivery_postal_code: '',
    delivery_city: '',
    delivery_location_type: 'appartement',
    delivery_floor: 0,
    delivery_elevator: false,
    delivery_to_floor: false, // Livraison sur pallier
    
    // ===== MOVING SPECIFIC =====
    // Departure - COMPLETE
    departure_address: '',
    departure_postal_code: '',
    departure_city: '',
    departure_location_type: 'appartement',
    departure_floor: 0,
    departure_elevator: false,
    departure_pickup_from_floor: false,
    
    // Arrival - COMPLETE
    arrival_address: '',
    arrival_postal_code: '',
    arrival_city: '',
    arrival_location_type: 'appartement',
    arrival_floor: 0,
    arrival_elevator: false,
    arrival_delivery_to_floor: false,
    
    // Heavy items for moving
    heavy_items: [],
    heavy_items_description: '',
    
    // ===== COMMON FIELDS =====
    // Cargo details
    package_count: '',
    furniture_count: '',
    estimated_volume: '',
    cargo_description: '', // Description détaillée du transport
    
    // Additional options
    urgent: false,
    extra_handling: false,
    furniture_protection: false,
    fragile_packing: false,
    
    // Notes
    additional_notes: ''
  });
  
  // Distance calculation
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  
  // Form submission
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  
  // Location types
  const locationTypes = [
    { value: 'appartement', label: 'Appartement' },
    { value: 'maison', label: 'Maison' },
    { value: 'entreprise', label: 'Entreprise / Bureau' },
    { value: 'local', label: 'Local commercial' },
    { value: 'entrepot', label: 'Entrepôt' }
  ];
  
  // Heavy items options
  const heavyItemsOptions = [
    'Réfrigérateur', 'Machine à laver', 'Sèche-linge', 'Lave-vaisselle',
    'Canapé', 'Lit (sommier + matelas)', 'Armoire', 'Piano', 'Coffre-fort',
    'Table', 'Bureau', 'Bibliothèque', 'Commode'
  ];
  
  // Build full address for distance calculation
  const getFullAddress = (type, direction) => {
    if (type === 'delivery') {
      if (direction === 'origin') {
        return `${formData.pickup_address}, ${formData.pickup_postal_code} ${formData.pickup_city}, France`;
      } else {
        return `${formData.delivery_address}, ${formData.delivery_postal_code} ${formData.delivery_city}, France`;
      }
    } else {
      if (direction === 'origin') {
        return `${formData.departure_address}, ${formData.departure_postal_code} ${formData.departure_city}, France`;
      } else {
        return `${formData.arrival_address}, ${formData.arrival_postal_code} ${formData.arrival_city}, France`;
      }
    }
  };
  
  // Calculate distance when addresses change
  const calculateDistance = useCallback(async () => {
    const origin = getFullAddress(quoteType, 'origin');
    const destination = getFullAddress(quoteType, 'destination');
    
    // Check if we have enough address info
    const hasOrigin = quoteType === 'delivery' 
      ? (formData.pickup_address && formData.pickup_postal_code)
      : (formData.departure_address && formData.departure_postal_code);
    const hasDestination = quoteType === 'delivery'
      ? (formData.delivery_address && formData.delivery_postal_code)
      : (formData.arrival_address && formData.arrival_postal_code);
    
    if (!hasOrigin || !hasDestination) {
      setDistanceInfo(null);
      return;
    }
    
    setCalculatingDistance(true);
    try {
      const response = await axios.post(`${API}/quotes/calculate-distance`, {
        origin,
        destination
      });
      
      if (response.data.success) {
        setDistanceInfo(response.data);
      } else {
        setDistanceInfo({ error: response.data.error });
      }
    } catch (err) {
      console.error('Distance calculation error:', err);
      setDistanceInfo({ error: 'Erreur de calcul de la distance' });
    } finally {
      setCalculatingDistance(false);
    }
  }, [quoteType, formData.pickup_address, formData.pickup_postal_code, formData.delivery_address, 
      formData.delivery_postal_code, formData.departure_address, formData.departure_postal_code,
      formData.arrival_address, formData.arrival_postal_code]);
  
  // Debounced distance calculation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (quoteType) {
        calculateDistance();
      }
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [calculateDistance, quoteType]);
  
  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleClientChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      client: {
        ...prev.client,
        [field]: value
      }
    }));
  };
  
  const handleHeavyItemToggle = (item) => {
    setFormData(prev => {
      const items = prev.heavy_items.includes(item)
        ? prev.heavy_items.filter(i => i !== item)
        : [...prev.heavy_items, item];
      return { ...prev, heavy_items: items };
    });
  };
  
  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    
    try {
      const endpoint = quoteType === 'delivery' ? '/quotes/delivery' : '/quotes/moving';
      
      // Build complete address objects
      let submitData;
      
      if (quoteType === 'delivery') {
        submitData = {
          quote_type: 'delivery',
          pickup_address: {
            address: formData.pickup_address,
            postal_code: formData.pickup_postal_code,
            city: formData.pickup_city,
            location_type: formData.pickup_location_type,
            floor: formData.pickup_floor,
            elevator: formData.pickup_elevator
          },
          pickup_from_floor: formData.pickup_from_floor,
          delivery_address: {
            address: formData.delivery_address,
            postal_code: formData.delivery_postal_code,
            city: formData.delivery_city,
            location_type: formData.delivery_location_type,
            floor: formData.delivery_floor,
            elevator: formData.delivery_elevator
          },
          delivery_to_floor: formData.delivery_to_floor,
          package_count: formData.package_count ? parseInt(formData.package_count) : null,
          furniture_count: formData.furniture_count ? parseInt(formData.furniture_count) : null,
          estimated_volume: formData.estimated_volume ? parseFloat(formData.estimated_volume) : null,
          cargo_description: formData.cargo_description,
          urgent: formData.urgent,
          extra_handling: formData.extra_handling,
          furniture_protection: formData.furniture_protection,
          fragile_packing: formData.fragile_packing,
          additional_notes: formData.additional_notes,
          client: formData.client
        };
      } else {
        submitData = {
          quote_type: 'moving',
          departure_address: {
            address: formData.departure_address,
            postal_code: formData.departure_postal_code,
            city: formData.departure_city,
            location_type: formData.departure_location_type,
            floor: formData.departure_floor,
            elevator: formData.departure_elevator
          },
          departure_pickup_from_floor: formData.departure_pickup_from_floor,
          arrival_address: {
            address: formData.arrival_address,
            postal_code: formData.arrival_postal_code,
            city: formData.arrival_city,
            location_type: formData.arrival_location_type,
            floor: formData.arrival_floor,
            elevator: formData.arrival_elevator
          },
          arrival_delivery_to_floor: formData.arrival_delivery_to_floor,
          heavy_items: formData.heavy_items,
          heavy_items_description: formData.heavy_items_description,
          package_count: formData.package_count ? parseInt(formData.package_count) : null,
          furniture_count: formData.furniture_count ? parseInt(formData.furniture_count) : null,
          estimated_volume: formData.estimated_volume ? parseFloat(formData.estimated_volume) : null,
          cargo_description: formData.cargo_description,
          urgent: formData.urgent,
          extra_handling: formData.extra_handling,
          furniture_protection: formData.furniture_protection,
          fragile_packing: formData.fragile_packing,
          additional_notes: formData.additional_notes,
          client: formData.client
        };
      }
      
      const response = await axios.post(`${API}${endpoint}`, submitData);
      
      if (response.data.success) {
        setSuccess(response.data);
      } else {
        setError(response.data.message || 'Une erreur est survenue');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi du formulaire');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Render quote type selection
  if (!quoteType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <img src={LOGO_URL} alt="DLV Express" className="h-12 cursor-pointer" onClick={() => navigate('/')} />
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au site
            </Button>
          </div>
        </header>
        
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Demande de devis gratuit</h1>
            <p className="text-lg text-slate-600">Choisissez le service qui correspond à votre besoin</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Transport Particuliers */}
            <button
              onClick={() => setQuoteType('delivery')}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-blue-500 text-left group"
              data-testid="quote-type-delivery"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-500 transition-colors">
                <Package className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Transport & Livraison</h2>
              <p className="text-slate-600 mb-4">
                Pour les particuliers. Transport de colis, mobilier, équipements, marchandises diverses.
              </p>
              <div className="flex items-center text-blue-600 font-medium">
                <span>Demander un devis</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </div>
            </button>
            
            {/* Déménagement */}
            <button
              onClick={() => setQuoteType('moving')}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-orange-500 text-left group"
              data-testid="quote-type-moving"
            >
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors">
                <Home className="w-8 h-8 text-orange-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Déménagement</h2>
              <p className="text-slate-600 mb-4">
                Service complet de déménagement avec manutention et transport sécurisé de vos biens.
              </p>
              <div className="flex items-center text-orange-600 font-medium">
                <span>Demander un devis</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </div>
            </button>
          </div>
          
          {/* Link to Enterprise contact */}
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Vous êtes une entreprise ?</p>
            <Button variant="outline" onClick={() => navigate('/contact-entreprise')} className="border-slate-300">
              <Briefcase className="w-4 h-4 mr-2" />
              Demande de contact professionnel
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Demande envoyée avec succès !</h2>
          <p className="text-slate-600 mb-6">{success.message}</p>
          
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {success.distance_km && (
                <div>
                  <span className="text-slate-500">Distance</span>
                  <p className="font-semibold text-slate-900">{success.distance_km} km</p>
                </div>
              )}
              {success.estimated_duration && (
                <div>
                  <span className="text-slate-500">Durée estimée</span>
                  <p className="font-semibold text-slate-900">{success.estimated_duration}</p>
                </div>
              )}
              {success.suggested_vehicle && (
                <div>
                  <span className="text-slate-500">Véhicule suggéré</span>
                  <p className="font-semibold text-slate-900">{success.suggested_vehicle}</p>
                </div>
              )}
            </div>
          </div>
          
          <p className="text-sm text-slate-500 mb-6">
            Nous étudions votre demande et vous recontacterons très rapidement avec un devis détaillé.
          </p>
          
          <Button onClick={() => navigate('/')} className="w-full">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }
  
  // Main form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="DLV Express" className="h-12 cursor-pointer" onClick={() => navigate('/')} />
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => setQuoteType(null)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Changer de service
            </Button>
          </div>
        </div>
      </header>
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            quoteType === 'delivery' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
          }`}>
            {quoteType === 'delivery' ? <Package className="w-4 h-4" /> : <Home className="w-4 h-4" />}
            {quoteType === 'delivery' ? 'Transport & Livraison' : 'Déménagement'}
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Demande de devis gratuit</h1>
          <p className="text-slate-600 mt-2">Remplissez le formulaire ci-dessous pour recevoir votre devis personnalisé</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Distance Info Card */}
          {(calculatingDistance || distanceInfo) && (
            <div className={`bg-white rounded-xl p-4 shadow-sm border-2 ${
              distanceInfo?.error ? 'border-red-200' : distanceInfo?.success ? 'border-green-200' : 'border-blue-200'
            }`}>
              {calculatingDistance ? (
                <div className="flex items-center gap-3 text-blue-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Calcul du trajet en cours...</span>
                </div>
              ) : distanceInfo?.error ? (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  <span>{distanceInfo.error}</span>
                </div>
              ) : distanceInfo?.success && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-8">
                    <div>
                      <span className="text-sm text-slate-500">Distance calculée</span>
                      <p className="text-2xl font-bold text-slate-900">{distanceInfo.distance_km} km</p>
                    </div>
                    <div>
                      <span className="text-sm text-slate-500">Temps de trajet estimé</span>
                      <p className="text-2xl font-bold text-slate-900">{distanceInfo.duration_str}</p>
                    </div>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              )}
            </div>
          )}
          
          {/* ===== ADDRESSES SECTION ===== */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Adresses
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* ORIGIN ADDRESS */}
              <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">A</div>
                  {quoteType === 'delivery' ? 'Adresse de prise en charge' : 'Adresse de départ'}
                </h3>
                
                <div>
                  <Label>Adresse (numéro et rue) *</Label>
                  <Input
                    value={quoteType === 'delivery' ? formData.pickup_address : formData.departure_address}
                    onChange={(e) => handleChange(quoteType === 'delivery' ? 'pickup_address' : 'departure_address', e.target.value)}
                    placeholder="Ex: 10 rue de la Paix"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Code postal *</Label>
                    <Input
                      value={quoteType === 'delivery' ? formData.pickup_postal_code : formData.departure_postal_code}
                      onChange={(e) => handleChange(quoteType === 'delivery' ? 'pickup_postal_code' : 'departure_postal_code', e.target.value)}
                      placeholder="75001"
                      maxLength={5}
                      required
                    />
                  </div>
                  <div>
                    <Label>Ville *</Label>
                    <Input
                      value={quoteType === 'delivery' ? formData.pickup_city : formData.departure_city}
                      onChange={(e) => handleChange(quoteType === 'delivery' ? 'pickup_city' : 'departure_city', e.target.value)}
                      placeholder="Paris"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Type de lieu *</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg bg-white"
                    value={quoteType === 'delivery' ? formData.pickup_location_type : formData.departure_location_type}
                    onChange={(e) => handleChange(quoteType === 'delivery' ? 'pickup_location_type' : 'departure_location_type', e.target.value)}
                    required
                  >
                    {locationTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Étage</Label>
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      value={quoteType === 'delivery' ? formData.pickup_floor : formData.departure_floor}
                      onChange={(e) => handleChange(quoteType === 'delivery' ? 'pickup_floor' : 'departure_floor', parseInt(e.target.value) || 0)}
                      placeholder="0 = RDC"
                    />
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={quoteType === 'delivery' ? formData.pickup_elevator : formData.departure_elevator}
                        onChange={(e) => handleChange(quoteType === 'delivery' ? 'pickup_elevator' : 'departure_elevator', e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span className="text-sm">Ascenseur</span>
                    </label>
                  </div>
                </div>
                
                <label className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer hover:border-blue-300">
                  <input
                    type="checkbox"
                    checked={quoteType === 'delivery' ? formData.pickup_from_floor : formData.departure_pickup_from_floor}
                    onChange={(e) => handleChange(quoteType === 'delivery' ? 'pickup_from_floor' : 'departure_pickup_from_floor', e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <span className="text-sm font-medium">Récupération sur pallier</span>
                </label>
              </div>
              
              {/* DESTINATION ADDRESS */}
              <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">B</div>
                  {quoteType === 'delivery' ? 'Adresse de livraison' : "Adresse d'arrivée"}
                </h3>
                
                <div>
                  <Label>Adresse (numéro et rue) *</Label>
                  <Input
                    value={quoteType === 'delivery' ? formData.delivery_address : formData.arrival_address}
                    onChange={(e) => handleChange(quoteType === 'delivery' ? 'delivery_address' : 'arrival_address', e.target.value)}
                    placeholder="Ex: 25 avenue des Champs-Élysées"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Code postal *</Label>
                    <Input
                      value={quoteType === 'delivery' ? formData.delivery_postal_code : formData.arrival_postal_code}
                      onChange={(e) => handleChange(quoteType === 'delivery' ? 'delivery_postal_code' : 'arrival_postal_code', e.target.value)}
                      placeholder="75008"
                      maxLength={5}
                      required
                    />
                  </div>
                  <div>
                    <Label>Ville *</Label>
                    <Input
                      value={quoteType === 'delivery' ? formData.delivery_city : formData.arrival_city}
                      onChange={(e) => handleChange(quoteType === 'delivery' ? 'delivery_city' : 'arrival_city', e.target.value)}
                      placeholder="Paris"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Type de lieu *</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg bg-white"
                    value={quoteType === 'delivery' ? formData.delivery_location_type : formData.arrival_location_type}
                    onChange={(e) => handleChange(quoteType === 'delivery' ? 'delivery_location_type' : 'arrival_location_type', e.target.value)}
                    required
                  >
                    {locationTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Étage</Label>
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      value={quoteType === 'delivery' ? formData.delivery_floor : formData.arrival_floor}
                      onChange={(e) => handleChange(quoteType === 'delivery' ? 'delivery_floor' : 'arrival_floor', parseInt(e.target.value) || 0)}
                      placeholder="0 = RDC"
                    />
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={quoteType === 'delivery' ? formData.delivery_elevator : formData.arrival_elevator}
                        onChange={(e) => handleChange(quoteType === 'delivery' ? 'delivery_elevator' : 'arrival_elevator', e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span className="text-sm">Ascenseur</span>
                    </label>
                  </div>
                </div>
                
                <label className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer hover:border-green-300">
                  <input
                    type="checkbox"
                    checked={quoteType === 'delivery' ? formData.delivery_to_floor : formData.arrival_delivery_to_floor}
                    onChange={(e) => handleChange(quoteType === 'delivery' ? 'delivery_to_floor' : 'arrival_delivery_to_floor', e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <span className="text-sm font-medium">Livraison sur pallier</span>
                </label>
              </div>
            </div>
          </div>
          
          {/* ===== CARGO DETAILS ===== */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Box className="w-5 h-5 text-blue-600" />
              {quoteType === 'delivery' ? 'Détails du transport' : 'Détails du déménagement'}
            </h2>
            
            {/* Heavy items for moving */}
            {quoteType === 'moving' && (
              <div className="mb-6">
                <Label className="mb-3 block">Objets lourds à transporter (sélectionnez si applicable)</Label>
                <div className="flex flex-wrap gap-2">
                  {heavyItemsOptions.map(item => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handleHeavyItemToggle(item)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.heavy_items.includes(item)
                          ? 'bg-orange-500 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                {formData.heavy_items.length > 0 && (
                  <div className="mt-3">
                    <Label>Précisions sur les objets lourds</Label>
                    <Input
                      value={formData.heavy_items_description}
                      onChange={(e) => handleChange('heavy_items_description', e.target.value)}
                      placeholder="Ex: Frigo américain, canapé d'angle 4 places..."
                    />
                  </div>
                )}
              </div>
            )}
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label>Nombre de colis / cartons</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.package_count}
                  onChange={(e) => handleChange('package_count', e.target.value)}
                  placeholder="Ex: 15"
                />
              </div>
              <div>
                <Label>Nombre de meubles</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.furniture_count}
                  onChange={(e) => handleChange('furniture_count', e.target.value)}
                  placeholder="Ex: 8"
                />
              </div>
              <div>
                <Label>Volume estimé (m³)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.estimated_volume}
                  onChange={(e) => handleChange('estimated_volume', e.target.value)}
                  placeholder="Ex: 10"
                />
              </div>
            </div>
            
            <div>
              <Label>Description détaillée du transport *</Label>
              <Textarea
                value={formData.cargo_description}
                onChange={(e) => handleChange('cargo_description', e.target.value)}
                placeholder={quoteType === 'delivery' 
                  ? "Décrivez les éléments à transporter : type de marchandises, dimensions approximatives, poids estimé, fragilité..."
                  : "Décrivez les biens à déménager : type de meubles, électroménager, objets fragiles ou de valeur..."
                }
                className="min-h-[100px]"
                required
              />
            </div>
          </div>
          
          {/* ===== OPTIONS ===== */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Options supplémentaires</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={formData.urgent}
                  onChange={(e) => handleChange('urgent', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <div>
                  <span className="font-medium">Transport urgent</span>
                  <p className="text-xs text-slate-500">Priorité de traitement</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={formData.extra_handling}
                  onChange={(e) => handleChange('extra_handling', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <div>
                  <span className="font-medium">Manutention supplémentaire</span>
                  <p className="text-xs text-slate-500">Aide au chargement/déchargement</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={formData.furniture_protection}
                  onChange={(e) => handleChange('furniture_protection', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <div>
                  <span className="font-medium">Protection du mobilier</span>
                  <p className="text-xs text-slate-500">Couvertures et protections</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={formData.fragile_packing}
                  onChange={(e) => handleChange('fragile_packing', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <div>
                  <span className="font-medium">Emballage objets fragiles</span>
                  <p className="text-xs text-slate-500">Protection renforcée</p>
                </div>
              </label>
            </div>
            
            <div className="mt-4">
              <Label>Remarques ou informations complémentaires</Label>
              <Textarea
                value={formData.additional_notes}
                onChange={(e) => handleChange('additional_notes', e.target.value)}
                placeholder="Informations utiles : accès difficile, code porte, horaires particuliers, contraintes..."
                className="min-h-[80px]"
              />
            </div>
          </div>
          
          {/* ===== CLIENT INFO ===== */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Vos coordonnées
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nom complet *</Label>
                <Input
                  value={formData.client.name}
                  onChange={(e) => handleClientChange('name', e.target.value)}
                  placeholder="Jean Dupont"
                  required
                />
              </div>
              <div>
                <Label>Téléphone *</Label>
                <Input
                  type="tel"
                  value={formData.client.phone}
                  onChange={(e) => handleClientChange('phone', e.target.value)}
                  placeholder="06 12 34 56 78"
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.client.email}
                  onChange={(e) => handleClientChange('email', e.target.value)}
                  placeholder="jean.dupont@email.com"
                  required
                />
              </div>
              <div>
                <Label>Date souhaitée</Label>
                <Input
                  type="date"
                  value={formData.client.preferred_date}
                  onChange={(e) => handleClientChange('preferred_date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Créneau horaire préféré</Label>
                <div className="flex gap-4 mt-2">
                  {[
                    { value: 'matin', label: 'Matin (8h-12h)' },
                    { value: 'apres-midi', label: 'Après-midi (14h-18h)' },
                    { value: 'journee', label: 'Journée complète' }
                  ].map(slot => (
                    <label key={slot.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="time_slot"
                        value={slot.value}
                        checked={formData.client.preferred_time_slot === slot.value}
                        onChange={(e) => handleClientChange('preferred_time_slot', e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{slot.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          {/* Submit button */}
          <div className="flex justify-center">
            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className={`px-12 py-6 text-lg ${quoteType === 'delivery' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-orange-600 hover:bg-orange-700'}`}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5 mr-2" />
                  Envoyer ma demande de devis
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuotePage;
